import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Resident } from '../types';
import { NotificationModal } from './NotificationModal';

interface EditMotorcyclePlateModalProps {
  isOpen: boolean;
  onClose: () => void;
  resident: Resident | null;
  onUpdateResident: (residentId: string, updates: Partial<Resident>) => Promise<boolean>;
}

export function EditMotorcyclePlateModal({ isOpen, onClose, resident, onUpdateResident }: EditMotorcyclePlateModalProps) {
  const [motorcyclePlate, setMotorcyclePlate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    visible: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({ visible: false, type: 'success', title: '', message: '' });

  // Initialize form with resident data
  useEffect(() => {
    if (resident) {
      setMotorcyclePlate(resident.motorcyclePlate || '');
    }
  }, [resident]);

  const resetForm = () => {
    setMotorcyclePlate('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resident) return;

    setIsSubmitting(true);
    try {
      const success = await onUpdateResident(resident.id, {
        motorcyclePlate: motorcyclePlate.trim() || undefined
      });

      if (success) {
        setNotification({
          visible: true,
          type: 'success',
          title: 'Berhasil!',
          message: `Nomor plat motor berhasil ${motorcyclePlate.trim() ? 'diperbarui' : 'dihapus'}.\n\nPlat Motor: ${motorcyclePlate.trim() || '(kosong)'}`
        });
      } else {
        setNotification({
          visible: true,
          type: 'error',
          title: 'Gagal Menyimpan',
          message: 'Gagal memperbarui nomor plat motor. Silakan coba lagi.'
        });
      }
    } catch (error) {
      console.error('Error updating motorcycle plate:', error);
      setNotification({
        visible: true,
        type: 'error',
        title: 'Terjadi Kesalahan',
        message: 'Terjadi kesalahan saat memperbarui data. Silakan coba lagi.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, visible: false });
    if (notification.type === 'success') {
      onClose();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !resident) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
          <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Edit Nomor Plat Motor
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Resident Info (Read-only) */}
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Nama:</span> {resident.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Kamar:</span> {resident.roomNumber} (Penghuni {resident.position})
              </p>
            </div>

            {/* Motorcycle Plate Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                🏍️ Nomor Plat Motor
              </label>
              <input
                type="text"
                value={motorcyclePlate}
                onChange={(e) => setMotorcyclePlate(e.target.value.toUpperCase())}
                placeholder="BA XXXX"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={25}
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Contoh: BA 1234, BK 5678, D 1111 (maksimal 25 karakter)
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                ℹ️ Sebagai penghuni, Anda hanya dapat mengubah nomor plat motor. 
                Untuk mengubah data lain, hubungi admin.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isSubmitting}
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Menyimpan...' : 'Simpan Plat Motor'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Notification Modal - Outside main container for proper z-index */}
      <NotificationModal
        visible={notification.visible}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={handleNotificationClose}
      />
    </>
  );
}
